/**
 * BE#282 — pure security helpers for the MCP OAuth consent screen
 * (mcp-oauth-page.tsx). Client registration is open by design, so the consent
 * screen is the last control against a malicious client; these back two of its
 * security properties.
 */

/**
 * Whether the request grants WRITE. Fails CLOSED: an empty or absent scope is
 * READ-ONLY, never the full write capability list. (Previously
 * `!scope || /write/i.test(scope)` fell open to write on an empty scope.)
 */
export function computeCanWrite(scope: string | null | undefined): boolean {
  return /write/i.test(scope || '');
}

/**
 * Extract the host (including any port) from a redirect_uri so the consent screen
 * can show the user WHERE their access will be sent — the single field that
 * reveals an attacker's callback. `valid:false` (empty host / unparseable /
 * non-hierarchical scheme like `javascript:`) means the screen must warn rather
 * than render a reassuring host.
 */
export function parseRedirectHost(
  redirectUri: string | null | undefined,
): { host: string; valid: boolean } {
  if (!redirectUri) return { host: '', valid: false };
  try {
    const host = new URL(redirectUri).host;
    return host ? { host, valid: true } : { host: '', valid: false };
  } catch {
    return { host: '', valid: false };
  }
}

/**
 * The capability cards to disclose for this grant. A read-only grant
 * (`canWrite === false`) must hide the WRITE cards so the disclosed capabilities
 * match the "Read-only access" badge, instead of showing write cards under it.
 */
export function selectVisibleCapabilities<T extends { write?: boolean }>(
  capabilities: T[],
  canWrite: boolean,
): T[] {
  return canWrite ? capabilities : capabilities.filter((c) => !c.write);
}

/** The access level the user picks on the consent screen (#755). */
export type McpAccessLevel = 'full' | 'read';

/**
 * #755: which access levels the consent screen offers. The user may only NARROW
 * the request: full access is offered only when the app asked for write, and the
 * matching option is preselected. An empty/missing scope counts as read+write,
 * because that is what the server grants for it (token endpoint default).
 * The backend re-checks this; a widening choice is rejected there.
 */
export function accessChoicesFor(scope: string | null | undefined): {
  fullAllowed: boolean;
  preselected: McpAccessLevel;
} {
  // Exact tokens, matching the backend's resolveGrantedScope: a loose /write/i match would
  // offer Full on e.g. 'mcp:read write', which the backend then rejects as widening.
  const tokens = (scope || '').split(/\s+/).filter(Boolean);
  const fullAllowed = tokens.length === 0 || tokens.includes('mcp:write') || tokens.includes('*');
  return { fullAllowed, preselected: fullAllowed ? 'full' : 'read' };
}
